import type { AppInfo } from '@/utils/app-type';
import { defineStore } from 'pinia';
import { getIp, getProjectConfig } from '@/api/common';
import { STATIC_URL } from '@/config/static-images';
import { ProjectEnum } from '@/project';
import { checkAppUpdate } from '@/service/upgrade';
import { useSiteStore } from '@/store/common/site';
import { useThemeStore } from '@/store/modules/theme';
import { AppType } from '@/utils/app-type';
import { getDeviceId } from '@/utils/device';
import { Storage } from '@/utils/storage';
import { systemInfo } from '@/utils/systemInfo';

interface AppState {
  systemInfo: any
  version: string
  os: string
  appType: number
  platform: string
  auth: object

  // 保存启动参数，用于后续跳转
  launchOptions: any

  // 项目配置是否加载完成
  isConfigLoaded: boolean
  configData: Partial<API.GetInfo>
  isInitializing: boolean

  apiUrl: string
  imgApiUrl: string
  logoUrl: string
  mUrl: string
  pcUrl: string
  staticUrl: string
  faviconIcoUrl: string

  type: 'dl' | '52dsy' | 'seller'
  independentSite: string // 独立站点编码，www或者空字符为主站

  // 配置就绪 Promise 机制
  configReadyPromise: Promise<void> | null
  _resolveConfigReady: ((value?: void) => void) | null
}

const app: AppInfo = AppType();
const env = import.meta.env;
const { VITE_APP_TYPE, VITE_APP_BASEURL } = env;

export const useAppStore = defineStore({
  id: 'app',
  state: (): AppState => ({
    systemInfo,
    version: systemInfo.appVersion,
    os: systemInfo.osName,
    appType: app.type,
    platform: app.platform,
    auth: {
      WRITE_EXTERNAL_STORAGE: {
        title: '储存空间/照片权限申请说明',
        content: '便于您使用该功能进行图片保存相册、客服聊天等场景中将内容保存到储存空间中。',
      },
      READ_EXTERNAL_STORAGE: {
        title: '储存空间权限申请说明',
        content: '便于您使用该功能上传您的照片/图片/视频及用于头像更换、图片搜索、图片保存、发布商品/分享、意见反馈、客服等场景中读取和储存内容。',
      },
      ACCESS_FINE_LOCATION: {
        title: '地理位置权限申请说明',
        content: '便于应用程序可以提供基于位置的服务、定位导航、附近搜索等功能。',
      },
      CAMERA: {
        title: '相机/摄像头权限申请说明',
        content: '便于您使用该功能您的摄像头用于头像更换、图片搜索、发布商品/分享、意见反馈、客服等场景中进行拍摄图片或视频。',
      },
      RECORD_AUDIO: {
        title: '麦克风权限申请说明',
        content: '便于您使用该功能进行录音、语音通话、发布语音、与客服语音沟通等场景中使用',
      },
      CALL_PHONE: {
        title: '拨打/管理电话权限申请说明',
        content: '便于您使用该功能联系买家、档口或者客服、业务经理与联系等场景下使用',
      },
      LIBRARY_CAMERA: {
        title: '相机/相册权限说明',
        content: '便于您使用该功能调用摄像头拍摄或者读取本地相册资源，用于头像更换、图片搜索、发布商品/分享、意见反馈、客服聊天等场景。',
      },
    },

    /* 项目配置是否加载完成 */
    isConfigLoaded: false,
    configData: {},
    isInitializing: false,
    launchOptions: null,

    type: '52dsy',
    independentSite: '',

    apiUrl: '',
    imgApiUrl: '',
    logoUrl: '',
    staticUrl: '',
    mUrl: '',
    pcUrl: '',
    faviconIcoUrl: '',

    // 配置就绪 Promise 机制
    configReadyPromise: null,
    _resolveConfigReady: null,
  }),
  getters: {
    getBarHeight(): number {
      if (systemInfo.platform === 'ios') {
        return 44 + systemInfo.statusBarHeight;
      }
      else if (systemInfo.platform === 'android') {
        return 48 + systemInfo.statusBarHeight;
      }
      else {
        return 0;
      }
    },
    // 获取项目域名
    getHost(): string {
      // 开发环境：使用环境变量 VITE_APP_BASEURL（根据 dev:h5:* 脚本自动切换）
      let hostname = VITE_APP_BASEURL as string;

      // #ifdef H5
      // 生产环境 H5：直接读取当前访问的域名
      if (import.meta.env.PROD) {
        hostname = window.location.hostname;
      }
      // #endif

      return hostname;
    },
    // 获取项目类型，与获取项目资源有关
    getProject(): ProjectEnum {
      // 从环境变量获取项目类型（根据 dev:h5:* 脚本自动切换）
      const envProjectType = VITE_APP_TYPE as ProjectEnum;

      // 生产环境：优先使用接口返回的配置数据精确判断项目类型
      if (import.meta.env.PROD && this.configData.type) {
        if (this.configData.type === '52dsy') {
          return this.configData.isIndependentSite ? ProjectEnum.SITE : ProjectEnum.MAIN;
        }
        else if (this.configData.type === 'dl') {
          return ProjectEnum.DAILI;
        }
        else if (this.configData.type === 'seller') {
          return ProjectEnum.SELLER;
        }
      }

      // 开发环境或未加载配置时：使用环境变量判断
      return envProjectType;
    },
    /** 是否为独立站（seller）模式 */
    isSellerProject(): boolean {
      return this.getProject === ProjectEnum.SELLER;
    },
    /** 项目代码（m / mdl / seller 等） */
    projectCode(): string {
      return this.getProject;
    },
    /** 主题主色 */
    mainColor(): string {
      const themeStore = useThemeStore();
      return themeStore.themeVars.colorTheme || '#dc1e1e';
    },
    /** 项目标题 */
    projectTitle(): string {
      return this.configData?.title || '';
    },
  },
  actions: {
    // 保存启动时的原始路径 (用于深层链接恢复)
    setLaunchOptions(options: any) {
      this.launchOptions = options;
    },

    // 初始化本地状态
    initLocalState() {
      // 初始化主题（按需）
      useThemeStore()?.initTheme();
    },

    // 初始化配置就绪 Promise
    initConfigReadyPromise() {
      if (!this.configReadyPromise) {
        this.configReadyPromise = new Promise<void>((resolve) => {
          this._resolveConfigReady = resolve;
        });
      }
    },

    // 等待配置就绪
    async waitForConfigReady() {
      if (this.isConfigLoaded) return;
      await this.configReadyPromise;
    },

    /* 获取项目配置 */
    async fetchConfig() {
      if (this.isConfigLoaded) return this.configData;
      this.isInitializing = true;
      try {
        const res = await getProjectConfig(this.getHost);
        console.log(res);
        this.configData = res;
        this.apiUrl = res.apiUrl;
        this.imgApiUrl = res.imgApiUrl;
        this.logoUrl = res.picUrl || '';
        this.mUrl = res.mUrl;
        this.pcUrl = res.pcUrl;
        this.staticUrl = STATIC_URL;
        this.faviconIcoUrl = res.faviconIcoUrl;
        if (!res.picUrl && (this.getProject === ProjectEnum.MAIN || this.getProject === ProjectEnum.SITE)) {
          // 设置项目logo
          this.logoUrl = 'https://m.52dsy.com/static/logo.png';
        }

        this.type = res.type;
        // 获取独立站点编码（非独立站点为空字符，独立站点为站点code）
        this.independentSite = res.type === '52dsy' && res.isIndependentSite ? res.dsySiteCode : '';

        // 项目配置完成（提前标记就绪，ip/设备指纹非启动必需，不阻塞启动链路）
        this.isConfigLoaded = true;
        this._resolveConfigReady?.();

        // 异步补齐 ip / 设备指纹，失败静默（请求头处已有 || '' 兜底）
        if (!Storage.get('ip')) {
          getIp().then((res) => {
            Storage.set('ip', res.ip);
          }).catch(() => { /* 静默 */ });
        }

        if (!Storage.get('fingerprint')) {
          getDeviceId().then((res) => {
            Storage.set('fingerprint', res);
          }).catch(() => { /* 静默 */ });
        }

        return res;
      }
      catch (error) {
        if (this.configData.url) {
          // 有缓存，先使用缓存
          this.isConfigLoaded = true;
          this._resolveConfigReady?.();
          return this.configData;
        }
        // 无缓存，抛出错误由外层跳转错误页兜底，避免静默卡死在启动页
        throw error;
      }
      finally {
        // 加载状态完成
        this.isInitializing = false;
      }
    },

    /* 获取项目其他内容 ，根据实际需要获取 */
    async fetchOtherConfig() {
      /* 获取项目站点相关内容 */
      await useSiteStore().fetchSiteList(this.independentSite);
    },

    /* 检测app版本号 */
    async checkVersion() {
      await checkAppUpdate(false);
    },

    /* 验证扫码登录 */
    async verifyScanLogin() {

    },

  },
  persist: {
    paths: ['configData', 'apiUrl', 'imgApiUrl', 'logoUrl'],
  },
});
